/**
 * DemoCarousel — wraps two (or more) terminal-demo slides with prev/next
 * arrows in the top-right and a fade-out → fade-in transition between them.
 *
 * Each slide is one of the gss-* typewriter demos (WorkflowDemo, AppsDemo,
 * McpDemo). Slides animate their typing on mount, so switching remounts the
 * active slide (bumped `key`) to replay the animation from scratch. The
 * outgoing slide fades out first; the incoming one fades in via its own
 * gss-wrapper mount animation.
 *
 * Slides are render functions (so callers can pass props), e.g.
 *   <DemoCarousel slides={[() => <WorkflowDemo />, () => <McpDemo context="pipeline" />]} />
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import './DemoCarousel.css';

const SWITCH_FADE_MS = 240;

const DemoCarousel = ({ slides }) => {
  const [index, setIndex] = useState(0);
  const [mountKey, setMountKey] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);
  const timerRef = useRef(null);
  const switchingRef = useRef(false);

  const go = useCallback((dir) => {
    const count = slides.length;
    if (count < 2 || switchingRef.current) return;
    switchingRef.current = true;
    setFadingOut(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + dir + count) % count);
      setMountKey((k) => k + 1);
      setFadingOut(false);
      switchingRef.current = false;
    }, SWITCH_FADE_MS);
  }, [slides.length]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!slides || slides.length === 0) return null;
  // slides[index] is a render function returning the slide element; clone it
  // with the carousel-controlled key so switching remounts → replays typing.
  const activeElement = React.cloneElement(slides[index](), { key: mountKey });

  return (
    <div className="demo-carousel">
      {slides.length > 1 && (
        <div className="demo-carousel-nav">
          <button
            type="button"
            className="demo-carousel-arrow"
            onClick={() => go(-1)}
            aria-label="Previous demo"
          >
            <IonIcon icon={chevronBackOutline} />
          </button>
          <button
            type="button"
            className="demo-carousel-arrow"
            onClick={() => go(1)}
            aria-label="Next demo"
          >
            <IonIcon icon={chevronForwardOutline} />
          </button>
        </div>
      )}
      <div className={`demo-carousel-stage ${fadingOut ? 'is-fading-out' : ''}`}>
        {activeElement}
      </div>
    </div>
  );
};

export default DemoCarousel;
